/**
 * Environmental Repository
 * COMPLETE implementation for environmental records
 */

import { BaseRepository } from '../BaseRepository';
import { db } from '../db';
import type { OilRecordEntry, GarbageRecordEntry, BallastWaterEntry, EmissionRecord } from '../../../types/database';

export class OilRecordRepository extends BaseRepository<OilRecordEntry> {
  constructor() {
    super(db.oilRecords, 'oilRecords');
  }

  async findByDateRange(startDate: Date, endDate: Date): Promise<OilRecordEntry[]> {
    return await this.table
      .where('date')
      .between(startDate, endDate, true, true)
      .toArray();
  }

  async findByOperationType(operationType: string): Promise<OilRecordEntry[]> {
    return await this.table.where('operationType').equals(operationType).toArray();
  }

  async getTotalDischarge(startDate: Date, endDate: Date): Promise<number> {
    const entries = await this.findByDateRange(startDate, endDate);
    return entries
      .filter(e => e.operationType.toLowerCase().includes('discharge'))
      .reduce((sum, e) => sum + e.quantity, 0);
  }
}

export class GarbageRecordRepository extends BaseRepository<GarbageRecordEntry> {
  constructor() {
    super(db.garbageRecords, 'garbageRecords');
  }

  async findByDateRange(startDate: Date, endDate: Date): Promise<GarbageRecordEntry[]> {
    return await this.table
      .where('date')
      .between(startDate, endDate, true, true)
      .toArray();
  }

  async findByGarbageType(garbageType: GarbageRecordEntry['garbageType']): Promise<GarbageRecordEntry[]> {
    if (!garbageType) return [];
    return await this.table.where('garbageType').equals(garbageType).toArray();
  }

  async findByDisposalMethod(method: GarbageRecordEntry['disposalMethod']): Promise<GarbageRecordEntry[]> {
    return await this.table.where('disposalMethod').equals(method).toArray();
  }
}

export class BallastWaterRepository extends BaseRepository<BallastWaterEntry> {
  constructor() {
    super(db.ballastRecords, 'ballastRecords');
  }

  async findByDateRange(startDate: Date, endDate: Date): Promise<BallastWaterEntry[]> {
    return await this.table
      .where('date')
      .between(startDate, endDate, true, true)
      .toArray();
  }

  async findByOperationType(operationType: BallastWaterEntry['operationType']): Promise<BallastWaterEntry[]> {
    if (!operationType) return [];
    return await this.table.where('operationType').equals(operationType).toArray();
  }
}

export class EmissionRecordRepository extends BaseRepository<EmissionRecord> {
  constructor() {
    super(db.emissionRecords, 'emissionRecords');
  }

  async findByDateRange(startDate: Date, endDate: Date): Promise<EmissionRecord[]> {
    return await this.table
      .where('date')
      .between(startDate, endDate, true, true)
      .toArray();
  }

  async findByFuelType(fuelType: string): Promise<EmissionRecord[]> {
    return await this.table.where('fuelType').equals(fuelType).toArray();
  }

  async getTotalEmissions(startDate: Date, endDate: Date): Promise<{
    co2: number;
    nox: number;
    sox: number;
  }> {
    const records = await this.findByDateRange(startDate, endDate);
    return {
      co2: records.reduce((sum, r) => sum + (r.co2 || 0), 0),
      nox: records.reduce((sum, r) => sum + (r.nox || 0), 0),
      sox: records.reduce((sum, r) => sum + (r.sox || 0), 0),
    };
  }
}

export const oilRecordRepository = new OilRecordRepository();
export const garbageRecordRepository = new GarbageRecordRepository();
export const ballastWaterRepository = new BallastWaterRepository();
export const emissionRecordRepository = new EmissionRecordRepository();


